import { RESPONSE_ERROR_PHRASES } from '@/consts/phrases/errorPhrases';
import { usePhrase } from '../hooks/general/usePhrase/usePhrase';
import { includes, isObject } from '@/types/typeGuards';
import { ErrorData, FetchErrorsType, FetchErrorsTypeSchema } from '@/types/types/general/errors';
import { FetchError } from './fetch/FetchError/FetchError';
import { isArrayWithValues, isEmpty } from 'utils/fp';
import { replaceVarsOnTranslation } from './replaceVarsOnTranslation';

type GeneralErrorStatusCode = 400 | 401 | 403 | 404 | 500 | 502 | 503;

type CreateFetchErrorsOptions = {
    phraseId?: number;
    message?: string;
    isMessageUserFriendly?: boolean;
    phrasePlaceholders?: (string | number)[];
    helpUrl?: string;
    data?: Record<string, unknown>;
    type?: 'error' | 'info' | 'success' | 'warning';
    property?: string;
};

const errorStatusToMessage: Record<GeneralErrorStatusCode, number> = {
    400: RESPONSE_ERROR_PHRASES.BAD_REQUEST,
    401: RESPONSE_ERROR_PHRASES.UNAUTHORISED,
    403: RESPONSE_ERROR_PHRASES.FORBIDDEN,
    404: RESPONSE_ERROR_PHRASES.NOT_FOUND,
    500: RESPONSE_ERROR_PHRASES.SERVER_ERROR,
    502: RESPONSE_ERROR_PHRASES.SERVER_ERROR,
    503: RESPONSE_ERROR_PHRASES.UNAVAILABLE,
};

const createFetchErrors = (options: CreateFetchErrorsOptions = {}): FetchErrorsType => {
    const {
        phraseId = RESPONSE_ERROR_PHRASES.UNKNOWN,
        message = 'UNKNOWN_ERROR',
        isMessageUserFriendly,
        phrasePlaceholders,
        helpUrl,
        data,
        type,
        property,
    } = options;

    const baseError = {
        phraseId,
        message,
        isMessageUserFriendly,
        phrasePlaceholders,
        helpUrl,
        data,
        type,
    };

    return property
        ? { propertyErrors: { [property]: [baseError] }, generalErrors: [] }
        : { propertyErrors: {}, generalErrors: [baseError] };
};

const getGeneralErrorMessageFromStatusCode = (code: number) =>
    includes([400, 401, 403, 404, 500, 502, 503] as const, code)
        ? errorStatusToMessage[code]
        : RESPONSE_ERROR_PHRASES.UNKNOWN;

export const isFetchError = (error: unknown): error is FetchError =>
    isObject(error) && 'status' in error && typeof error.status === 'number';

export const getErrorDetail = (
    potentialError: unknown,
    detailName: string,
    propertyName?: string,
    errorIdx?: number,
) => {
    const getSpecificErrorDetail = (error: ErrorData) => {
        return error?.data?.[detailName];
    };

    const fetchError = FetchErrorsTypeSchema.safeParse(potentialError).success
        ? (potentialError as FetchErrorsType)
        : getFetchErrors(potentialError);

    if (fetchError) {
        if (propertyName) {
            const propertyData = fetchError?.propertyErrors?.[propertyName][errorIdx ?? 0];
            return propertyData ? getSpecificErrorDetail(propertyData) : undefined;
        } else {
            return (
                getSpecificErrorDetail(fetchError?.generalErrors[errorIdx ?? 0]) ??
                (fetchError?.propertyErrors &&
                    Object.values(fetchError?.propertyErrors)[0]?.[errorIdx ?? 0] &&
                    getSpecificErrorDetail(
                        Object.values(fetchError?.propertyErrors)[0]?.[errorIdx ?? 0],
                    ))
            );
        }
    }
    return undefined;
};

const getLegacyFetchError = (fetchError: FetchError): FetchErrorsType =>
    createFetchErrors({
        phraseId: fetchError.phraseId || getGeneralErrorMessageFromStatusCode(fetchError.status),
        phrasePlaceholders:
            fetchError.phrasePlaceholders && fetchError.phrasePlaceholders.map((x) => String(x)),
        message: fetchError?.message,
    });

export const getFetchErrors = (potentialError: unknown): FetchErrorsType => {
    if (!isFetchError(potentialError)) {
        return createFetchErrors();
    }

    const fetchError = potentialError;

    if (!fetchError?.errors) {
        return getLegacyFetchError(fetchError);
    }

    if (!isArrayWithValues(fetchError.errors)) {
        return createFetchErrors();
    }

    const fetchErrors: FetchErrorsType = {
        propertyErrors: {},
        generalErrors: [],
    };

    const getFieldName = (property?: string, path?: string[]) =>
        !path?.length ? property : [...path, property].join('.');

    for (const error of fetchError.errors) {
        const {
            property,
            path,
            message,
            isMessageUserFriendly,
            phraseId,
            type,
            phrasePlaceholders,
            helpUrl,
            data,
        } = error;

        const errorOptions = {
            phraseId: phraseId ? phraseId : getGeneralErrorMessageFromStatusCode(fetchError.status),
            message,
            isMessageUserFriendly,
            phrasePlaceholders:
                phrasePlaceholders && !isEmpty(phrasePlaceholders) ? phrasePlaceholders : undefined,
            helpUrl,
            data,
            property,
            type,
        };

        const newErrors = createFetchErrors(errorOptions);
        const errorKey = getFieldName(property, path);

        if (newErrors?.propertyErrors && property && errorKey) {
            if (fetchErrors.propertyErrors[errorKey]) {
                fetchErrors.propertyErrors[errorKey].push(...newErrors.propertyErrors[property]);
            } else {
                fetchErrors.propertyErrors[errorKey] = newErrors.propertyErrors[property];
            }
        }
        if (newErrors && newErrors.generalErrors) {
            fetchErrors.generalErrors.push(...newErrors.generalErrors);
        }
    }

    return !isEmpty(fetchErrors.propertyErrors) || !isEmpty(fetchErrors.generalErrors)
        ? fetchErrors
        : createFetchErrors();
};

export const getGeneralFetchErrorPhraseId = (potentialError: unknown): number | undefined =>
    getFetchErrors(potentialError)?.generalErrors[0]?.phraseId;

interface FormInputError {
    type: 'custom';
    message: string;
}

export const getErrorMessageText = (
    error: Partial<ErrorData> | undefined,
    translateFn: ReturnType<typeof usePhrase>['t'],
) => {
    if (error?.isMessageUserFriendly && error?.message) {
        return replaceVarsOnTranslation(error.message, error?.phrasePlaceholders);
    }
    if (error?.phraseId) {
        return translateFn(error?.phraseId, {
            replaceTextVars: error?.phrasePlaceholders,
            asString: true,
        });
    }
    return '';
};

export const getFormInputError = (
    potentialError: unknown,
    translateFn: ReturnType<typeof usePhrase>['t'],
    property?: string,
): FormInputError => {
    const fetchErrors = getFetchErrors(potentialError);

    const errorToUse = property
        ? fetchErrors?.propertyErrors[property][0]
        : fetchErrors?.generalErrors[0];

    return {
        type: 'custom',
        message: getErrorMessageText(errorToUse, translateFn),
    };
};
